import type { Lesson, Progress, Syllabus, Topic } from "../../services/curriculumService";
import { byNumber, byText, humanise, reversed, type RegisterConfig } from "../register/registerLogic";

/**
 * Syllabus planning: the directory of syllabi, each one's teaching sequence
 * of topics and lessons, and how far classes have got through it.
 */

export const DIFFICULTIES: [string, string][] = [
  ["beginner", "Beginner"],
  ["intermediate", "Intermediate"],
  ["advanced", "Advanced"],
];
export const LESSON_TYPES: [string, string][] = [
  ["theory", "Theory"],
  ["practical", "Practical"],
  ["assessment", "Assessment"],
  ["revision", "Revision"],
  ["field_trip", "Field trip"],
  ["project", "Project"],
];
export const labelFrom = (pairs: [string, string][], value?: string | null) => pairs.find(([key]) => key === value)?.[1] ?? humanise(value);

export const activeStatus = (record: { is_active: boolean }) =>
  record.is_active ? { label: "Active", dot: "bg-emerald-500" } : { label: "Inactive", dot: "bg-gray-400" };

/** Topics in the order they are taught, then by name. Order is taken as
 * stored -- some schools number from 0 -- and shown as a position from 1. */
export const inTeachingOrder = <T extends Pick<Topic, "order" | "name">>(topics: T[]) =>
  [...topics].sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.name.localeCompare(b.name, undefined, { numeric: true }));

export const lessonsInOrder = (lessons: Lesson[]) =>
  [...lessons].sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.title.localeCompare(b.title, undefined, { numeric: true }));

// --- Coverage ------------------------------------------------------------------------------

export const progressStatus = (row: Pick<Progress, "status">) => {
  const map: Record<string, { label: string; dot: string }> = {
    not_started: { label: "Not started", dot: "bg-gray-400" },
    in_progress: { label: "Being taught", dot: "bg-sky-500" },
    completed: { label: "Taught", dot: "bg-emerald-500" },
    delayed: { label: "Behind", dot: "bg-amber-500" },
    cancelled: { label: "Dropped", dot: "bg-gray-400" },
  };
  return map[row.status] ?? { label: humanise(row.status), dot: "bg-gray-400" };
};

/**
 * How far one class has got through a syllabus: the share of its topics that
 * class has finished, counting a topic under way by its percentage. A class
 * with no record yet has covered nothing.
 */
export function classCoverage(topicIds: number[], records: Pick<Progress, "topic" | "status" | "progress_percentage">[]) {
  if (!topicIds.length) return 0;
  const byTopic = new Map<number, number>();
  for (const record of records) {
    const share = record.status === "completed" ? 100 : record.status === "cancelled" ? 0 : Number(record.progress_percentage) || 0;
    byTopic.set(record.topic, Math.max(byTopic.get(record.topic) ?? 0, share));
  }
  const total = topicIds.reduce((sum, id) => sum + (byTopic.get(id) ?? 0), 0);
  return Math.round(total / topicIds.length);
}

/** Coverage for each class that has records against the syllabus, least covered first. */
export function coverageByClass(topics: Pick<Topic, "id">[], records: Progress[]) {
  const ids = topics.map((topic) => topic.id);
  const classes = new Map<number, { id: number; name: string; records: Progress[] }>();
  for (const record of records) {
    const entry = classes.get(record.class_group) ?? { id: record.class_group, name: record.class_name, records: [] };
    entry.records.push(record);
    classes.set(record.class_group, entry);
  }
  return [...classes.values()]
    .map((entry) => ({
      id: entry.id,
      name: entry.name,
      coverage: classCoverage(ids, entry.records),
      taught: new Set(entry.records.filter((record) => record.status === "completed").map((record) => record.topic)).size,
      teachers: [...new Set(entry.records.map((record) => record.teacher_name).filter(Boolean))],
    }))
    .sort((a, b) => a.coverage - b.coverage || a.name.localeCompare(b.name));
}

// --- The directory -------------------------------------------------------------------------

export type SyllabusRow = Syllabus & { topicCount: number; lessonCount: number; weeks: number; coverage: number | null };

export function withPlanCounts(syllabi: Syllabus[] | undefined, topics: Topic[] | undefined, lessons: Lesson[] | undefined, progress: Progress[] | undefined): SyllabusRow[] | undefined {
  return syllabi?.map((syllabus) => {
    const own = (topics ?? []).filter((topic) => topic.syllabus === syllabus.id);
    const ids = new Set(own.map((topic) => topic.id));
    const byClass = coverageByClass(own, (progress ?? []).filter((record) => record.syllabus === syllabus.id));
    return {
      ...syllabus,
      topicCount: own.length,
      lessonCount: (lessons ?? []).filter((lesson) => ids.has(lesson.topic)).length,
      weeks: own.reduce((sum, topic) => sum + (topic.duration_weeks || 0), 0),
      coverage: byClass.length ? Math.round(byClass.reduce((sum, row) => sum + row.coverage, 0) / byClass.length) : null,
    };
  });
}

/** "P5", "Primary 5", "Baby" -- in school order for the pick-list and sorting. */
export function levelRank(level: string) {
  const text = level.toLowerCase();
  const nursery = ["baby", "middle", "top"].findIndex((name) => text.startsWith(name));
  if (nursery >= 0) return nursery;
  const number = /^(?:p|primary)\s*(\d+)/.exec(text) ?? /^(?:s|senior)\s*(\d+)/.exec(text);
  if (number) return (text.startsWith("s") ? 20 : 10) + Number(number[1]);
  return 100;
}

export const syllabusConfig: RegisterConfig<SyllabusRow> = {
  search: (row) => [row.title, row.subject_name, row.subject_code, row.grade_name, row.curriculum_name, row.description].join(" "),
  views: [
    { key: "active", label: "Active", match: (row) => row.is_active },
    { key: "mine", label: "Yours to plan", match: (row) => row.is_active && Boolean(row.can_edit) },
    { key: "empty", label: "No topics yet", match: (row) => row.is_active && row.topicCount === 0 },
    { key: "inactive", label: "Inactive", match: (row) => !row.is_active },
    { key: "all", label: "All" },
  ],
  category: { label: "Class level", value: (row) => row.grade_name },
  sorts: [
    { key: "level", label: "Class level, then subject", compare: (a, b) => levelRank(a.grade_name) - levelRank(b.grade_name) || a.grade_name.localeCompare(b.grade_name) || a.subject_name.localeCompare(b.subject_name) },
    { key: "subject", label: "Subject, then class level", compare: (a, b) => a.subject_name.localeCompare(b.subject_name) || levelRank(a.grade_name) - levelRank(b.grade_name) },
    { key: "coverage", label: "Least covered first", compare: byNumber<SyllabusRow>((row) => row.coverage ?? -1) },
    { key: "-updated", label: "Recently changed", compare: reversed(byText<SyllabusRow>((row) => row.updated_at ?? "")) },
  ],
};
